import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useBandStore } from '../../stores/bandStore';
import type { Band } from '../../models/types';
import { useMemo, useEffect, useState } from 'react';

// Month number to name mapping
const monthNames: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

const getMonthName = (monthNum: string): string => {
  return monthNames[monthNum] || monthNum;
};

interface BandCustomizationModalProps {
  open: boolean;
  onClose: () => void;
  band: Band | null;
}

const BandCustomizationModal = ({ open, onClose, band }: BandCustomizationModalProps) => {
  const {
    isYearSelected,
    isMonthYearSelected,
    toggleYearSelection,
    toggleMonthYearSelection,
    selectedBandYears,
    selectedBandMonthYears,
    isBandSelected,
    toggleBandSelection,
  } = useBandStore();

  // Track initial state to allow cancel/revert
  const [initialYears, setInitialYears] = useState<Set<string> | null>(null);
  const [initialMonthYears, setInitialMonthYears] = useState<Set<string> | null>(null);
  const [wasBandSelected, setWasBandSelected] = useState<boolean>(false);

  // Capture initial state when modal opens
  useEffect(() => {
    if (open && band) {
      const currentYears = selectedBandYears.get(band.bandName);
      const currentMonthYears = selectedBandMonthYears.get(band.bandName);
      
      setInitialYears(currentYears ? new Set(currentYears) : new Set<string>());
      setInitialMonthYears(currentMonthYears ? new Set(currentMonthYears) : new Set<string>());
      setWasBandSelected(isBandSelected(band));
    }
  }, [open, band, selectedBandYears, selectedBandMonthYears, isBandSelected]);

  if (!band) return null;

  const handleSave = () => {
    // Make sure the band is selected
    if (!isBandSelected(band)) {
      toggleBandSelection(band);
    }
    onClose();
  };

  const handleCancel = () => {
    // Revert to initial state
    if (band && initialYears !== null && initialMonthYears !== null) {
      const store = useBandStore.getState();
      const currentYears = selectedBandYears.get(band.bandName) || new Set<string>();
      const currentMonthYears = selectedBandMonthYears.get(band.bandName) || new Set<string>();

      // Remove years that weren't in initial state
      for (const year of currentYears) {
        if (!initialYears.has(year)) {
          store.toggleYearSelection(band, year);
        }
      }

      // Add years that were in initial state but got removed
      for (const year of initialYears) {
        if (!currentYears.has(year)) {
          store.toggleYearSelection(band, year);
        }
      }

      // Remove month-years that weren't in initial state
      for (const monthYear of currentMonthYears) {
        if (!initialMonthYears.has(monthYear)) {
          store.toggleMonthYearSelection(band, monthYear);
        }
      }

      // Add month-years that were in initial state but got removed
      for (const monthYear of initialMonthYears) {
        if (!currentMonthYears.has(monthYear)) {
          store.toggleMonthYearSelection(band, monthYear);
        }
      }

      // If band wasn't selected initially and isn't selected now, make sure it stays unselected
      // If it got selected during customization, deselect it
      if (!wasBandSelected && isBandSelected(band)) {
        toggleBandSelection(band);
      }
    }
    onClose();
  };

  const availableYears = band.years || [];
  const availableYearMonths = band.yearMonths || [];

  // Group year-months by year
  const yearMonthsByYear = useMemo(() => {
    const grouped = new Map<string, string[]>();
    availableYearMonths.forEach((yearMonth) => {
      const year = yearMonth.split('-')[0];
      if (!grouped.has(year)) {
        grouped.set(year, []);
      }
      grouped.get(year)!.push(yearMonth);
    });
    return grouped;
  }, [availableYearMonths]);

  // Get selected years that have month-year options
  const selectedYearsWithMonths = useMemo(() => {
    return availableYears.filter((year) => yearMonthsByYear.has(year));
  }, [availableYears, yearMonthsByYear]);

  // Get years that are selected AND have month-years available
  const expandedYears = useMemo(() => {
    const bandSelectedYears = selectedBandYears.get(band.bandName) || new Set<string>();
    return selectedYearsWithMonths.filter((year) => bandSelectedYears.has(year));
  }, [selectedYearsWithMonths, band, selectedBandYears]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Customize {band.bandName}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Select specific years to focus your shuffle. For years with monthly data, you can
          further customize by selecting specific months after selecting the year.
        </Typography>

        {/* Years Section */}
        {availableYears.length > 0 && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              Years
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Click a year to select it. Years with monthly options will show a dropdown when
              selected.
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {availableYears.map((year) => {
                const isSelected = isYearSelected(band, year);
                const hasMonthOptions = yearMonthsByYear.has(year);

                return (
                  <Chip
                    key={year}
                    label={year}
                    onClick={() => toggleYearSelection(band, year)}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{
                      '&.MuiChip-filled': {
                        bgcolor: hasMonthOptions ? 'primary.main' : 'primary.main',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* Month-Year Sections - Only show for selected years */}
        {expandedYears.length > 0 && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              Monthly Options
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select specific months within your chosen years for more precise control.
            </Typography>

            {expandedYears.map((year) => {
              const monthOptions = yearMonthsByYear.get(year) || [];
              const selectedMonths =
                selectedBandMonthYears.get(band.bandName) || new Set<string>();
              const yearSelectedMonths = Array.from(selectedMonths).filter((ym) =>
                ym.startsWith(year)
              );

              return (
                <Accordion
                  key={year}
                  defaultExpanded={true}
                  sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: 'rgba(66, 165, 245, 0.08)',
                      borderRadius: 1,
                      '&.Mui-expanded': {
                        borderRadius: '4px 4px 0 0',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        pr: 2,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {year}
                      </Typography>
                      {yearSelectedMonths.length > 0 && (
                        <Chip
                          label={`${yearSelectedMonths.length} month${yearSelectedMonths.length > 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 24, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 2 }}>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {monthOptions.map((yearMonth) => {
                        const monthNum = yearMonth.split('-')[1];
                        const monthName = getMonthName(monthNum);
                        return (
                          <Chip
                            key={yearMonth}
                            label={monthName}
                            onClick={() => toggleMonthYearSelection(band, yearMonth)}
                            color={isMonthYearSelected(band, yearMonth) ? 'secondary' : 'default'}
                            variant={isMonthYearSelected(band, yearMonth) ? 'filled' : 'outlined'}
                          />
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

        {/* Selection Summary */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          Current Selection
        </Typography>

        <List dense>
          {availableYears.filter((year) => isYearSelected(band, year)).length > 0 && (
            <ListItem>
              <ListItemText
                primary="Selected Years"
                secondary={availableYears.filter((year) => isYearSelected(band, year)).join(', ')}
              />
            </ListItem>
          )}

          {availableYearMonths.filter((ym) => isMonthYearSelected(band, ym)).length > 0 && (
            <ListItem>
              <ListItemText
                primary="Selected Year-Months"
                secondary={availableYearMonths
                  .filter((ym) => isMonthYearSelected(band, ym))
                  .map((ym) => {
                    const [year, month] = ym.split('-');
                    return `${year} ${getMonthName(month)}`;
                  })
                  .join(', ')}
              />
            </ListItem>
          )}
        </List>

        {availableYears.filter((year) => isYearSelected(band, year)).length === 0 &&
          availableYearMonths.filter((ym) => isMonthYearSelected(band, ym)).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No customizations selected - will use random selection from all available content.
            </Typography>
          )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BandCustomizationModal;
